import React, { useEffect, useState } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import FormField from '../../components/ui/FormField';
import StatusBanner from '../../components/ui/StatusBanner';

/**
 * Module 2 Commit 6 — inline expiration editor.
 *
 * Reuses the existing PATCH /api/employees/documents/:id/expiration
 * contract: { expiresAt } XOR { clear: true }. Clearing requires a
 * separate explicit click (not just emptying the field + saving) so
 * the audit trail captures intent unambiguously.
 */
function isoDateOnly(iso) {
  if (!iso) return '';
  const trimmed = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : '';
}

export default function EditDocumentExpirationModal({ open, onClose, doc, onSubmit }) {
  const initial = isoDateOnly(doc?.expiresAt);
  const [value, setValue] = useState(initial);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Re-sync local state whenever the modal opens (the parent may
  // pass a different doc between opens).
  useEffect(() => {
    if (open) {
      setValue(isoDateOnly(doc?.expiresAt));
      setError('');
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id, doc?.expiresAt]);

  const hasExisting = Boolean(doc?.expiresAt);
  const dirty = value !== initial;

  const submit = async (payload) => {
    setIsSubmitting(true);
    setError('');
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err?.message || 'Unable to update expiration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () => {
    if (!value) {
      setError('Choose a date or clear the expiration explicitly.');
      return;
    }
    submit({ expiresAt: value });
  };

  const handleClear = () => {
    submit({ clear: true });
  };

  return (
    <Modal
      open={open}
      onClose={isSubmitting ? () => {} : onClose}
      title={hasExisting ? 'Edit expiration' : 'Set expiration'}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!value || !dirty || isSubmitting}
          >
            {isSubmitting ? 'Saving…' : (hasExisting ? 'Update' : 'Set expiration')}
          </Button>
        </>
      }
    >
      {error ? <StatusBanner kind="error" message={error} /> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField
          label="Expiration date"
          htmlFor="edit-document-expires-at"
          hint="Used to flag expiring certifications in the documents panel."
        >
          <Input
            id="edit-document-expires-at"
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="edit-document-expires-at-input"
          />
        </FormField>
        {hasExisting ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClear}
              disabled={isSubmitting}
              data-testid="edit-document-clear-button"
            >
              Clear expiration
            </Button>
            <span style={{ fontSize: 11, color: '#9ba2b1' }}>
              Removes the expiration date entirely. Audited as a separate action.
            </span>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
